import { useSyncExternalStore } from 'react';

import { sendMessageStream } from './api';
import { notifyChatConversationsChanged } from './events';
import { liveActivityFromToolCall } from './tool-display';
import type { LiveToolActivity, Message } from './types';
import type { ChatTurnDebugTrace } from './turn-debug';

export const CHAT_STREAM_COMPLETED = 'couplespace:chat-stream-completed';

export type ChatStreamCompletedDetail = {
  conversationId: string;
  aborted: boolean;
};

export type ChatStreamSnapshot = {
  streamingText: string;
  liveActivities: LiveToolActivity[];
  liveActivityCollapsed: boolean;
  streamError: string | null;
  optimisticUserMessage: Message;
};

type StreamCallbacks = {
  prepare?: () => Promise<void>;
  onDebugTrace?: (trace: ChatTurnDebugTrace) => void;
};

type ActiveStream = {
  abortController: AbortController;
  snapshot: ChatStreamSnapshot;
  callbacks: StreamCallbacks;
};

const activeStreams = new Map<string, ActiveStream>();
const listeners = new Set<() => void>();

function emitChange(): void {
  for (const listener of listeners) {
    listener();
  }
}

function notifyStreamCompleted(conversationId: string, aborted: boolean): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent<ChatStreamCompletedDetail>(CHAT_STREAM_COMPLETED, {
        detail: { conversationId, aborted },
      }),
    );
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function isConversationStreaming(conversationId: string): boolean {
  return activeStreams.has(conversationId);
}

export function getConversationStreamSnapshot(
  conversationId: string,
): ChatStreamSnapshot | null {
  return activeStreams.get(conversationId)?.snapshot ?? null;
}

export function useConversationStreaming(conversationId: string | undefined): boolean {
  return useSyncExternalStore(
    subscribe,
    () => (conversationId ? isConversationStreaming(conversationId) : false),
    () => false,
  );
}

export function useConversationStreamSnapshot(
  conversationId: string | undefined,
): ChatStreamSnapshot | null {
  return useSyncExternalStore(
    subscribe,
    () => (conversationId ? getConversationStreamSnapshot(conversationId) : null),
    () => null,
  );
}

export function stopConversationStream(conversationId: string): void {
  activeStreams.get(conversationId)?.abortController.abort();
}

export function toggleStreamLiveActivityCollapsed(conversationId: string): void {
  const active = activeStreams.get(conversationId);
  if (!active) {
    return;
  }
  active.snapshot = {
    ...active.snapshot,
    liveActivityCollapsed: !active.snapshot.liveActivityCollapsed,
  };
  emitChange();
}

export async function startConversationStream(
  conversationId: string,
  text: string,
  callbacks: StreamCallbacks = {},
): Promise<void> {
  if (activeStreams.has(conversationId)) {
    return;
  }

  const optimisticUserMessage: Message = {
    id: `optimistic-${Date.now()}`,
    conversationId,
    role: 'user',
    content: { text },
    toolName: null,
    model: null,
    promptTokens: null,
    completionTokens: null,
    contextTokens: null,
    cost: null,
    createdAt: new Date().toISOString(),
  };

  const abortController = new AbortController();
  const snapshot: ChatStreamSnapshot = {
    streamingText: '',
    liveActivities: [],
    liveActivityCollapsed: false,
    streamError: null,
    optimisticUserMessage,
  };

  activeStreams.set(conversationId, {
    abortController,
    snapshot,
    callbacks,
  });
  emitChange();

  try {
    await callbacks.prepare?.();

    await sendMessageStream(
      conversationId,
      text,
      (event) => {
        const active = activeStreams.get(conversationId);
        if (!active) {
          return;
        }

        if (event.type === 'token') {
          active.snapshot = {
            ...active.snapshot,
            streamingText: `${active.snapshot.streamingText}${event.delta}`,
          };
        }
        if (event.type === 'tool_call') {
          active.snapshot = {
            ...active.snapshot,
            liveActivities: [
              ...active.snapshot.liveActivities,
              liveActivityFromToolCall(
                event.name,
                event.args,
                `${event.name}-${active.snapshot.liveActivities.length}`,
              ),
            ],
          };
        }
        if (event.type === 'tool_result') {
          active.snapshot = {
            ...active.snapshot,
            liveActivities: active.snapshot.liveActivities.map((activity) =>
              activity.name === event.name && activity.status === 'running'
                ? {
                    ...activity,
                    status: event.ok ? 'ok' : 'error',
                    summary: event.summary,
                  }
                : activity,
            ),
          };
        }
        if (event.type === 'debug') {
          active.callbacks.onDebugTrace?.(event.trace);
        }
        if (event.type === 'error') {
          active.snapshot = {
            ...active.snapshot,
            streamError: event.message,
          };
        }
        if (event.type === 'done') {
          active.snapshot = {
            ...active.snapshot,
            liveActivityCollapsed: true,
          };
          if (event.title) {
            notifyChatConversationsChanged();
          }
        }

        emitChange();
      },
      abortController.signal,
    );
  } catch (err) {
    const active = activeStreams.get(conversationId);
    if (active && !(err instanceof DOMException && err.name === 'AbortError')) {
      active.snapshot = {
        ...active.snapshot,
        streamError: err instanceof Error ? err.message : 'Failed to send message',
      };
      emitChange();
    }
  } finally {
    const aborted = abortController.signal.aborted;
    activeStreams.delete(conversationId);
    emitChange();
    notifyStreamCompleted(conversationId, aborted);
    if (!aborted) {
      notifyChatConversationsChanged();
    }
  }
}
